import { useEffect, useMemo, useState } from "react"
import { selectUserDetails } from "../../redux/slices/auth"
import { checkIsBankAdmin, checkIsSuperAdmin } from "../utils"
import { useAppDispatch, useAppSelector } from "./use-store"
import {
  setSelectedBrand,
  clearSelectedBrand,
  selectSelectedBrand
} from "../../redux/slices/conversations"
import {
  useGetThreadMessagesQuery,
  useGetThreadsQuery
} from "../../redux/queries"
import { useUserLastActivity } from "./use-user-last-activity"

const useConversations = () => {
  const dispatch = useAppDispatch()

  const lastActivity = useUserLastActivity()
  const user = useAppSelector(selectUserDetails)
  const isSuperAdmin = checkIsSuperAdmin(user?.roles ?? [])
  const isBankAdmin = checkIsBankAdmin(user?.roles ?? [])

  const [searchQuery, setSearchQuery] = useState<string>("")

  // Manage brand selection based on user role
  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(clearSelectedBrand())
    } else {
      dispatch(setSelectedBrand(Number(user?.brand_id)))
    }
  }, [dispatch, isSuperAdmin, user?.brand_id])

  const selectedBrand = useAppSelector(selectSelectedBrand)

  // Prepare query params
  const queryParam = useMemo(() => {
    if (isSuperAdmin) {
      return { brand_id: selectedBrand ?? undefined }
    }
    if (isBankAdmin) {
      return { brand_id: user?.brand_id ?? undefined }
    }
    return { loid: user?.id }
  }, [isSuperAdmin, isBankAdmin, selectedBrand, user])

  // queryParam = { loid: "6309" }

  // Fetch threads and new messages
  const {
    data: threads = [],
    error,
    isError,
    isFetching,
    isLoading,
    refetch
  } = useGetThreadsQuery(queryParam)

  const {
    data: newThreads = [],
    error: newError,
    isError: newIsError,
    isFetching: newIsFetching,
    isLoading: newIsLoading
  } = useGetThreadMessagesQuery(queryParam, {
    pollingInterval: 10000,
    skip: Date.now() - lastActivity > 180000 // Polling every 10s if recent activity (3 min)
  })

  const newMessages = useMemo(() => {
    console.log("newThreads", newThreads.length)
    let updatedMessages = [...threads]

    // loop through newThreads and check if there are new messages, if so, find the thread in threads and update the messages array
    if (newThreads.length > 0 && typeof updatedMessages !== "undefined") {
      newThreads.forEach(newThread => {
        console.log("new message:", newThread)
        const threadIndex = updatedMessages.findIndex(
          thread => thread._id === newThread.thread_id
        )
        if (threadIndex !== -1) {
          const thread = updatedMessages[threadIndex]

          // Check if the messages are different
          if (thread.messages.length !== newThread.new_messages.length) {
            // Immutable update - replace the thread with a new object that has updated messages
            updatedMessages = updatedMessages.map((t, i) =>
              i === threadIndex
                ? {
                    ...t,
                    messages: [...t.messages, ...newThread.new_messages],
                    hasNewMessages: true
                  }
                : t
            )
          }
        } else {
          // If the thread is not found, call refetch
          refetch()
        }
      })
    }

    return updatedMessages
  }, [threads, newThreads, refetch])

  return {
    showBrandSelector: isSuperAdmin,
    threads: newThreads.length ? newMessages : threads,
    error: error || newError,
    isError: isError || newIsError,
    isFetching: isFetching || newIsFetching,
    isLoading: isLoading || newIsLoading,
    playNewMessageSound: newThreads.length > 0,
    searchQuery,
    setSearchQuery
  }
}

export default useConversations
